import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';

/**
 * Medical Documents — everything the SERVER holds for this patient, in one
 * list: prescriptions, lab reports, imaging orders, vaccinations, provider
 * certificates and the files in the Health Vault.
 *
 * This page used to render the in-browser store, so a patient with real
 * records on the server was told "No health records yet". Each source is
 * loaded independently: one failing endpoint costs its own section, never the
 * whole page. Verification is only ever claimed where the server says so —
 * a certificate carries its own `verificationStatus`; nothing else is labelled.
 */

type CategoryKey = 'all' | 'prescriptions' | 'lab' | 'imaging' | 'vaccinations' | 'certificates' | 'uploads' | 'pending';

interface DocumentItem {
  id: string;
  docType: string;
  category: Exclude<CategoryKey, 'all' | 'pending'>;
  accentColor: string;
  title: string;
  source: string;
  date: string;
  statusLabel: string;
  statusColor: string;
  /** True only when the record is finished / available to read. */
  ready: boolean;
  /** The server's verification verdict; absent means none is claimed. */
  verified?: boolean;
  detail?: string;
  route: string;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Map a backend status to the badge the list shows. */
function badgeFor(status: string, ready: boolean): { label: string; color: string } {
  const s = (status || '').toUpperCase();
  if (s.includes('CANCEL')) return { label: 'Cancelled', color: T.danger };
  if (ready) return { label: 'Available', color: T.success };
  if (s.includes('PEND') || s.includes('REQUEST') || s.includes('SCHEDUL') || s.includes('ORDER')) {
    return { label: 'Pending', color: T.warning };
  }
  return { label: s ? s.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase()) : 'Processing', color: T.info };
}

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'prescriptions', label: 'Prescriptions' },
  { key: 'lab', label: 'Lab Reports' },
  { key: 'imaging', label: 'Imaging' },
  { key: 'vaccinations', label: 'Vaccinations' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'uploads', label: 'Self Uploads' },
  { key: 'pending', label: 'Pending' },
];

export default function PatientDocuments() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<CategoryKey>('all');
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedDoc(null);
    const [rx, labs, imaging, vaccines, certs, vault] = await Promise.allSettled([
      patientApi.getPrescriptions(),
      patientApi.getLabReports(),
      patientApi.getImagingOrders(),
      patientApi.getVaccinations(),
      patientApi.getCertificates(),
      patientApi.getHealthVault(),
    ]);

    const items: DocumentItem[] = [];
    const failed: string[] = [];

    if (rx.status === 'fulfilled') {
      for (const p of rx.value ?? []) {
        const ready = (p.status || '').toUpperCase() !== 'DRAFT';
        const badge = badgeFor(p.status, ready);
        items.push({
          id: `rx-${p.id}`, docType: 'Prescription', category: 'prescriptions', accentColor: T.navy,
          title: p.diagnosis || p.chiefComplaint || 'Prescription',
          source: [p.doctorName, p.clinicName].filter(Boolean).join(' · ') || 'Doctor',
          date: p.signedAt || p.appointmentDate || p.createdAt,
          statusLabel: badge.label, statusColor: badge.color, ready,
          detail: `${(p.medicines ?? []).length} medicine${(p.medicines ?? []).length === 1 ? '' : 's'}`,
          route: '/patient/prescriptions',
        });
      }
    } else failed.push('prescriptions');

    if (labs.status === 'fulfilled') {
      for (const l of labs.value ?? []) {
        items.push({
          id: `lab-${l.id}`, docType: 'Lab Report', category: 'lab', accentColor: T.teal,
          title: l.testName || 'Lab report', source: l.labName || 'Laboratory',
          date: l.reportDate,
          statusLabel: l.isCritical ? 'Critical' : 'Available',
          statusColor: l.isCritical ? T.danger : T.success,
          ready: true,
          detail: l.criticalCount > 0
            ? `${l.criticalCount} critical finding${l.criticalCount === 1 ? '' : 's'}`
            : undefined,
          route: '/patient/lab-reports',
        });
      }
    } else failed.push('lab reports');

    if (imaging.status === 'fulfilled') {
      for (const o of imaging.value ?? []) {
        const badge = badgeFor(o.status, o.reportAvailable);
        items.push({
          id: `img-${o.id}`, docType: 'Imaging', category: 'imaging', accentColor: T.purple,
          title: [o.modality, o.bodyPart].filter(Boolean).join(' · ') || o.testName || 'Imaging study',
          source: o.facility || o.orderedBy || 'Imaging centre',
          date: o.scheduledFor || o.orderedAt || '',
          statusLabel: o.critical ? 'Critical' : badge.label,
          statusColor: o.critical ? T.danger : badge.color,
          ready: o.reportAvailable,
          detail: o.orderNumber ? `Order ${o.orderNumber}` : undefined,
          route: '/patient/diagnostics',
        });
      }
    } else failed.push('imaging');

    if (vaccines.status === 'fulfilled') {
      for (const v of (vaccines.value ?? []).filter(v => v.status !== 'REMOVED')) {
        items.push({
          id: `vac-${v.id}`, docType: 'Vaccination', category: 'vaccinations', accentColor: T.success,
          title: [v.vaccineName, v.doseLabel].filter(Boolean).join(' · '),
          source: v.administeredAt || (v.source === 'SELF_REPORTED' ? 'Recorded by you' : 'Provider'),
          date: v.administeredOn || v.createdAt,
          statusLabel: 'Recorded', statusColor: T.success, ready: true,
          detail: v.nextDueDate ? `Next dose due ${formatDate(v.nextDueDate)}` : undefined,
          route: '/patient/vaccinations',
        });
      }
    } else failed.push('vaccinations');

    if (certs.status === 'fulfilled') {
      for (const c of certs.value ?? []) {
        const verified = String((c as { verificationStatus?: string }).verificationStatus ?? '').toUpperCase() === 'VERIFIED';
        items.push({
          id: `cert-${c.id}`, docType: 'Certificate', category: 'certificates', accentColor: T.info,
          title: c.title || c.certificateType,
          source: c.issuerName || c.issuerType || 'Provider',
          date: (c as { issueDate?: string; createdAt?: string }).issueDate ?? (c as { createdAt?: string }).createdAt ?? '',
          statusLabel: verified ? 'NeoVerified' : 'Issued',
          statusColor: verified ? T.success : T.info,
          ready: true,
          verified,
          detail: (c as { neoVerifyId?: string }).neoVerifyId ?? undefined,
          route: `/patient/certificates/preview/${c.id}`,
        });
      }
    } else failed.push('certificates');

    if (vault.status === 'fulfilled') {
      for (const d of vault.value ?? []) {
        items.push({
          id: `vault-${d.id}`, docType: 'Self Upload', category: 'uploads', accentColor: T.textSec,
          title: d.title || d.fileName || 'Document',
          source: d.issuedBy || 'Uploaded by you',
          date: d.issuedAt || (d as { createdAt?: string }).createdAt || '',
          statusLabel: 'In Health Vault', statusColor: T.teal, ready: true,
          detail: d.fileName ?? undefined,
          route: '/patient/health-vault',
        });
      }
    } else failed.push('vault uploads');

    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setDocs(items);
    setFailedSources(failed);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = docs.filter(doc => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return !doc.ready;
    return doc.category === activeTab;
  });

  return (
    <div style={{ ...govPageContainer({ maxWidth: 1100 }) }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingTop: 4 }}>
        <div>
          <h1 style={{ ...GOV_TYPE.pageTitle }}>Medical Documents</h1>
          <p style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>
            Prescriptions, lab reports, imaging, vaccinations and certificates — all in one place
          </p>
        </div>
        <button onClick={() => navigate('/patient/health-vault')} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: T.teal, color: T.white, border: 'none', borderRadius: 10,
          padding: '8px 16px', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
        }}>
          + Upload in Health Vault
        </button>
      </div>

      {/* A source that did not answer is named — the rest still lists. */}
      {failedSources.length > 0 && (
        <div style={{
          background: `${T.warning}0C`, border: `1px solid ${T.warning}35`, borderRadius: 12,
          padding: 10, marginBottom: 12, fontSize: 10, color: T.text, fontFamily: T.font,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <span>Could not load: {failedSources.join(', ')}. Everything else is shown.</span>
          <button onClick={() => void load()} style={{
            fontSize: 10, fontWeight: 700, fontFamily: T.font, color: T.white,
            background: T.warning, border: 'none', borderRadius: 8, padding: '5px 12px', cursor: 'pointer',
          }}>Retry</button>
        </div>
      )}

      {/* Category Tabs */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
        {CATEGORIES.map(cat => {
          const count = cat.key === 'all'
            ? docs.length
            : cat.key === 'pending'
              ? docs.filter(d => !d.ready).length
              : docs.filter(d => d.category === cat.key).length;
          return (
            <button
              key={cat.key}
              onClick={() => { setActiveTab(cat.key); setSelectedDoc(null); }}
              style={{
                padding: '6px 14px', fontSize: 11, fontFamily: T.font,
                fontWeight: activeTab === cat.key ? 700 : 500,
                color: activeTab === cat.key ? T.white : T.textSec,
                background: activeTab === cat.key ? T.teal : T.white,
                border: activeTab === cat.key ? 'none' : `1px solid ${T.border}`,
                borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {cat.label}{count > 0 ? ` · ${count}` : ''}
            </button>
          );
        })}
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{
              textAlign: 'center', padding: '48px 20px', background: T.white,
              border: `1px solid ${T.border}`, borderRadius: 16,
              fontSize: 11, color: T.textSec, fontFamily: T.font,
            }}>
              Loading your records…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 20px', background: T.white,
              border: `1px solid ${T.border}`, borderRadius: 16,
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.2" strokeLinecap="round" style={{ margin: '0 auto 12px', display: 'block' }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                {docs.length === 0 ? 'No health records yet' : 'Nothing in this category'}
              </div>
              <div style={{ fontSize: 11, color: T.textSec, marginBottom: 16, lineHeight: 1.6 }}>
                {docs.length === 0
                  ? 'Your prescriptions, lab reports, and medical documents will appear here as you use NeoHealthCard services.'
                  : 'Choose another category to see the rest of your records.'}
              </div>
              {docs.length === 0 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/patient/care-services/doctors')} style={{
                    padding: '9px 18px', fontSize: 11, fontWeight: 700, fontFamily: T.font,
                    color: T.white, background: T.teal, border: 'none', borderRadius: 10, cursor: 'pointer',
                  }}>Visit a Doctor</button>
                  <button onClick={() => navigate('/patient/care-services/labs')} style={{
                    padding: '9px 18px', fontSize: 11, fontWeight: 700, fontFamily: T.font,
                    color: T.teal, background: T.white, border: `1px solid ${T.teal}`, borderRadius: 10, cursor: 'pointer',
                  }}>Book Lab Test</button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(doc => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  style={{
                    background: T.white, border: `1px solid ${selectedDoc?.id === doc.id ? T.teal : T.border}`,
                    borderRadius: 14, padding: 14, boxShadow: T.shadow, cursor: 'pointer',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: `${doc.accentColor}10`, display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}>
                      <div style={{ width: 10, height: 10, borderRadius: 5, background: doc.accentColor }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: doc.accentColor }}>{doc.docType}</span>
                        <span style={{
                          fontSize: 9, fontWeight: 600, color: T.white, background: doc.statusColor,
                          padding: '2px 8px', borderRadius: 10,
                        }}>{doc.statusLabel}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.title}
                      </div>
                      <div style={{ fontSize: 10, color: T.textSec, marginBottom: 2 }}>{doc.source}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 9, color: T.textTer }}>{formatDate(doc.date)}</span>
                        {doc.detail && <span style={{ fontSize: 9, color: T.textTer }}>· {doc.detail}</span>}
                      </div>

                      {doc.verified && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
                          fontSize: 9, fontWeight: 600, color: T.success, background: `${T.success}10`,
                          padding: '2px 8px', borderRadius: 6,
                        }}>
                          NeoVerified by {doc.source}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={e => { e.stopPropagation(); navigate(doc.route); }} style={{
                      flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 600, fontFamily: T.font,
                      color: T.white, background: T.teal, border: 'none', borderRadius: 10, cursor: 'pointer',
                    }}>Open</button>
                    <button onClick={e => { e.stopPropagation(); navigate('/patient/consent'); }} style={{
                      flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 600, fontFamily: T.font,
                      color: T.teal, background: T.white, border: `1px solid ${T.teal}`, borderRadius: 10, cursor: 'pointer',
                    }}>Share</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel — desktop */}
        <div style={{
          width: 340, flexShrink: 0, position: 'sticky', top: 20,
          display: selectedDoc ? 'flex' : 'none', flexDirection: 'column', gap: 12,
        }}>
          {selectedDoc && (
            <div style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
              padding: 16, boxShadow: T.shadow,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>Document Detail</span>
                <button onClick={() => setSelectedDoc(null)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: T.textTer, padding: 0,
                }}>✕</button>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{selectedDoc.title}</div>
              <div style={{ fontSize: 11, color: T.textSec, marginBottom: 2 }}>{selectedDoc.docType} · {selectedDoc.source}</div>
              <div style={{ fontSize: 10, color: T.textTer, marginBottom: 12 }}>{formatDate(selectedDoc.date)}</div>
              {selectedDoc.detail && (
                <div style={{ fontSize: 10, color: T.textSec, marginBottom: 12 }}>{selectedDoc.detail}</div>
              )}
              <button onClick={() => navigate(selectedDoc.route)} style={{
                width: '100%', padding: '9px 0', fontSize: 11, fontWeight: 700, fontFamily: T.font,
                color: T.white, background: T.teal, border: 'none', borderRadius: 10, cursor: 'pointer', marginBottom: 10,
              }}>
                Open full record
              </button>
              <div style={{
                background: `${T.teal}06`, border: `1px solid ${T.teal}18`, borderRadius: 10,
                padding: 10, fontSize: 9, color: T.textSec, lineHeight: 1.6, textAlign: 'center',
              }}>
                {selectedDoc.verified
                  ? 'This certificate carries a NeoVerify id issued by the provider.'
                  : 'NeoVerify verification is issued by the provider; it appears here only once they issue it.'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Footer */}
      <div style={{
        marginTop: 20, background: `${T.teal}06`, border: `1px solid ${T.teal}18`, borderRadius: 12,
        padding: 12, fontSize: 9, color: T.textSec, lineHeight: 1.6, textAlign: 'center',
      }}>
        Every access is logged. Sharing requires consent or emergency authorization. Provider-verified documents appear in Certificates and Health Vault.
      </div>
    </div>
  );
}
